import { GlobalVariables } from './constants';

/** Canonical persisted bucket role tokens (API / Dynamo Name values). */
export const BUCKET_ROLE_HOME = '{{home}}';
export const BUCKET_ROLE_CACHE = '{{cache}}';
export const BUCKET_ROLE_FILE_PICKUP = '{{filepickup}}';
export const BUCKET_ROLE_SMB = 'smb';

const VALID_ROLE_TOKENS = new Set([
  BUCKET_ROLE_HOME,
  BUCKET_ROLE_CACHE,
  BUCKET_ROLE_FILE_PICKUP,
]);

/** Bare slug / token-inner → canonical token (edit UX for pre-token data). */
const LEGACY_INNER_TO_TOKEN: Record<string, string> = {
  home: BUCKET_ROLE_HOME,
  cache: BUCKET_ROLE_CACHE,
  filepickup: BUCKET_ROLE_FILE_PICKUP,
  'file-pickup': BUCKET_ROLE_FILE_PICKUP,
};

export const BUCKET_ROLE_LABELS: Record<string, string> = {
  [BUCKET_ROLE_HOME]: 'Home',
  [BUCKET_ROLE_CACHE]: 'Cache',
  [BUCKET_ROLE_FILE_PICKUP]: 'File pickup',
};

/** PULL filecopy internal S3 destination role select (UI only; persisted as token or SMB preview name). */
export const PULL_FILECOPY_DEST_ROLE_FORM_OPTIONS = [
  { value: 'home', name: BUCKET_ROLE_LABELS[BUCKET_ROLE_HOME] },
  { value: 'filepickup', name: BUCKET_ROLE_LABELS[BUCKET_ROLE_FILE_PICKUP] },
  { value: 'smb', name: 'SMB share' },
] as const;

/** True for pull filemove destination SMB role slug or persisted SMB preview name. */
export function isPullSmbDestinationBucket(name: string): boolean {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  return trimmed.toLowerCase() === BUCKET_ROLE_SMB || isSmbBucketPreviewName(trimmed);
}

/** True for persisted SMB preview names (`{SMB_BUCKET_NAME}-{buSlug}`), not role tokens. */
export function isSmbBucketPreviewName(name: string): boolean {
  if (!name || typeof name !== 'string') return false;
  const smbPrefix = GlobalVariables.SMB_BUCKET_NAME;
  if (!smbPrefix || smbPrefix === 'undefined') return false;
  return name.trim().startsWith(`${smbPrefix}-`);
}

/** True only for canonical persisted tokens (`{{home}}`, `{{cache}}`, `{{filepickup}}`). Case-insensitive. */
export function isValidBucketRoleToken(name: string): boolean {
  if (!name || typeof name !== 'string') return false;
  const trimmed = name.trim();
  if (isSmbBucketPreviewName(trimmed)) return false;
  return VALID_ROLE_TOKENS.has(trimmed) || VALID_ROLE_TOKENS.has(trimmed.toLowerCase());
}

/**
 * Map persisted step bucket `name` to a canonical role token when possible.
 * Accepts tokens (case-normalized), bare legacy slugs, and efm-* literals.
 * Returns null for SMB preview names and unrecognized values.
 */
export function resolveLegacyBucketRoleToken(name: string): string | null {
  if (!name || typeof name !== 'string') return null;
  const trimmed = name.trim();
  if (!trimmed || isSmbBucketPreviewName(trimmed)) return null;
  if (isValidBucketRoleToken(trimmed)) return trimmed.toLowerCase();

  const tokenMatch = trimmed.match(/^\{\{([^}]+)\}\}$/);
  if (tokenMatch) {
    const inner = tokenMatch[1].trim().toLowerCase();
    const fromInner = LEGACY_INNER_TO_TOKEN[inner];
    if (fromInner) return fromInner;
  }

  const lower = trimmed.toLowerCase();
  const fromSlug = LEGACY_INNER_TO_TOKEN[lower];
  if (fromSlug) return fromSlug;

  const awsHome = (GlobalVariables.AWS_BUCKET_NAME ?? '').trim().toLowerCase();
  const isEfmPrefixedLiteral = lower.startsWith('efm-');
  const withoutDr = lower.endsWith('-dr') ? lower.slice(0, -3) : lower;
  if (lower === awsHome || (isEfmPrefixedLiteral && (withoutDr.endsWith('-home') || withoutDr.endsWith('-archive')))) {
    return BUCKET_ROLE_HOME;
  }
  if (isEfmPrefixedLiteral && (withoutDr.endsWith('-cache') || withoutDr.endsWith('-home-pull'))) {
    return BUCKET_ROLE_CACHE;
  }
  if (isEfmPrefixedLiteral && (withoutDr.endsWith('-file-pickup') || withoutDr.endsWith('-destination'))) {
    return BUCKET_ROLE_FILE_PICKUP;
  }

  return null;
}
